import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import LanguageToggle from "../core/LanguageToggle";
import { useTranslation } from "react-i18next";

import {
  ClassRoster,
  RosterStudent,
  getClassRoster,
  loginAsClassStudent,
  loginWithPin,
  setStudentPin,
} from "../../../services/api";

type Step = "code" | "student" | "pin" | "new-pin";

interface Props {
  open: boolean;
  onClose: () => void;
  onAuthenticated: (token: string) => void;
}

const PIN_LENGTH = 4;

/**
 * A class Chromebook gets used by the same class every day, so the code is
 * remembered on that device and the second visit starts at the name list.
 * Wrapped because a locked-down browser can throw on any storage access.
 */
const REMEMBERED_CODE_KEY = "shanenli_class_code";

const readRememberedCode = (): string => {
  try {
    return localStorage.getItem(REMEMBERED_CODE_KEY) ?? "";
  } catch {
    return "";
  }
};

const writeRememberedCode = (code: string) => {
  try {
    localStorage.setItem(REMEMBERED_CODE_KEY, code);
  } catch {
    // A device that will not remember is still a device that can sign in.
  }
};

const forgetRememberedCode = () => {
  try {
    localStorage.removeItem(REMEMBERED_CODE_KEY);
  } catch {
    // Nothing to do.
  }
};

const digitsOnly = (value: string) =>
  value.replace(/\D/g, "").slice(0, PIN_LENGTH);

/**
 * Types the hyphen for them: three letters, then it appears on its own.
 * A child copying off a whiteboard should not have to find the key.
 */
const formatClassCode = (raw: string) => {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
  const letters = cleaned.slice(0, 3);
  const digits = cleaned.slice(3);
  return digits ? `${letters}-${digits}` : letters;
};

/**
 * Shaped like a real code but impossible to issue: the generator never puts a
 * 1 or a 0 in one, because children misread them.
 */
const EXAMPLE_CODE = "ABC-1234";

/**
 * Sign-in for students on a shared school device: class code, own name,
 * four digit PIN. No email, no Google.
 */
const ClassCodeLoginModal: React.FC<Props> = ({
  open,
  onClose,
  onAuthenticated,
}) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>("code");
  const [classCode, setClassCode] = useState("");
  const [roster, setRoster] = useState<ClassRoster | null>(null);
  const [student, setStudent] = useState<RosterStudent | null>(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  const reset = () => {
    setStep("code");
    setClassCode("");
    setRoster(null);
    setStudent(null);
    setPin("");
    setConfirmPin("");
    setError(null);
    setBusy(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  /**
   * The server answers in Hebrew, so showing its message put Hebrew on an
   * English screen. Every message a child can see is decided here instead,
   * from the status code, and translated like the rest of the page.
   */
  const messageFrom = (err: any, fallback: string) => {
    switch (err?.response?.status) {
      case 404:
        return t("classCodeNotFound");
      case 423:
        return t("pinLockedMessage");
      case 429:
        return t("tooManyTries");
      default:
        return fallback;
    }
  };

  const loadRoster = useCallback(
    async (code: string, { silent = false }: { silent?: boolean } = {}) => {
      setBusy(true);
      setError(null);
      try {
        const data = await getClassRoster(code);
        setRoster(data);
        setStep("student");
        if (remember) writeRememberedCode(code);
        return true;
      } catch (err: any) {
        // A remembered code that no longer works just puts them back on the
        // code screen, without an error a child cannot act on.
        forgetRememberedCode();
        if (!silent) setError(messageFrom(err, t("classCodeNotFound")));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [remember, t]
  );

  const submitCode = () => loadRoster(classCode.trim());

  // Same class, same device, next day: start at the name list.
  useEffect(() => {
    if (!open) return;
    const saved = readRememberedCode();
    if (!saved) return;
    setClassCode(saved);
    loadRoster(saved, { silent: true });
    // Only on open: re-running this would fight the user's own navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chooseStudent = async (chosen: RosterStudent) => {
    setStudent(chosen);
    setPin("");
    setConfirmPin("");
    setError(null);

    if (roster && !roster.requires_pin) {
      setBusy(true);
      try {
        const { token } = await loginAsClassStudent({
          student_id: chosen.id,
          class_code: classCode.trim(),
        });
        reset();
        onAuthenticated(token);
        return;
      } catch (err: any) {
        if (!err?.response?.data?.requires_pin) {
          setError(messageFrom(err, t("errorOccurred")));
          setBusy(false);
          return;
        }
        // The class was switched to personal codes since the roster loaded.
      } finally {
        setBusy(false);
      }
    }

    setStep(chosen.has_pin ? "pin" : "new-pin");
  };

  const submitPin = async () => {
    if (!student) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await loginWithPin({
        student_id: student.id,
        class_code: classCode.trim(),
        pin,
      });
      reset();
      onAuthenticated(token);
    } catch (err: any) {
      if (err?.response?.data?.needs_pin) {
        setPin("");
        setStep("new-pin");
        setBusy(false);
        return;
      }
      setPin("");
      setError(messageFrom(err, t("wrongPin")));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPin = async () => {
    if (!student) return;
    if (pin !== confirmPin) {
      setError(t("pinsDoNotMatch"));
      setConfirmPin("");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { token } = await setStudentPin({
        student_id: student.id,
        class_code: classCode.trim(),
        pin,
      });
      reset();
      onAuthenticated(token);
    } catch (err: any) {
      setPin("");
      setConfirmPin("");
      setError(
        err?.response?.status === 409
          ? t("pinAlreadySet")
          : messageFrom(err, t("pinTooEasy"))
      );
    } finally {
      setBusy(false);
    }
  };

  const title = useMemo(() => {
    switch (step) {
      case "code":
        return t("enterClassCode");
      case "student":
        return t("chooseYourName");
      case "pin":
        return t("enterYourPin");
      default:
        return t("chooseYourPin");
    }
  }, [step, t]);

  const useDifferentClass = () => {
    forgetRememberedCode();
    setClassCode("");
    setRoster(null);
    setError(null);
    setStep("code");
  };

  const back = () => {
    setError(null);
    if (step === "student") setStep("code");
    if (step === "pin" || step === "new-pin") setStep("student");
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="xs"
      className="class-code-dialog"
    >
      <Box className="dialog-controls">
        {step !== "code" && (
          <IconButton onClick={back} size="small" aria-label={t("back")}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }} />
        {/* Right where someone gets stuck, not on the page behind the dialog. */}
        <LanguageToggle />
        <IconButton onClick={close} size="small" aria-label={t("close")}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === "code" && (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); submitCode(); }}>
            <TextField
              autoFocus
              fullWidth
              value={classCode}
              onChange={(e) => setClassCode(formatClassCode(e.target.value))}
              placeholder={EXAMPLE_CODE}
              inputProps={{
                style: {
                  fontSize: 32,
                  letterSpacing: 4,
                  textAlign: "center",
                  fontFamily: "monospace",
                },
                maxLength: 8,
                autoCapitalize: "characters",
                autoCorrect: "off",
              }}
            />
            <Typography
              variant="body2"
              sx={{ mt: 1, opacity: 0.6, textAlign: "center" }}
            >
              {t("classCodeExample", { code: EXAMPLE_CODE })}
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => {
                    setRemember(e.target.checked);
                    if (!e.target.checked) forgetRememberedCode();
                  }}
                />
              }
              label={t("rememberClassCode")}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={busy || classCode.trim().length < 8}
              sx={{ mt: 1, py: 1.5, fontSize: 18 }}
            >
              {t("continue")}
            </Button>
          </Box>
        )}

        {step === "student" && roster && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.7 }}>
              {[roster.class_name, roster.school_name].filter(Boolean).join(", ")}
            </Typography>
            <Box sx={{ display: "grid", gap: 1 }}>
              {roster.students.map((s) => (
                <Button
                  key={s.id}
                  variant="outlined"
                  size="large"
                  onClick={() => chooseStudent(s)}
                  disabled={busy}
                  sx={{ justifyContent: "flex-start", py: 1.5, fontSize: 18 }}
                >
                  {s.name}
                </Button>
              ))}
              {roster.students.length === 0 && (
                <Typography>{t("noStudentsInClass")}</Typography>
              )}
            </Box>
            <Button
              onClick={useDifferentClass}
              size="small"
              sx={{ mt: 1.5 }}
              disabled={busy}
            >
              {t("differentClass")}
            </Button>
          </Box>
        )}

        {step === "pin" && student && (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); submitPin(); }}>
            <Typography sx={{ mb: 1.5 }}>{student.name}</Typography>
            <TextField
              autoFocus
              fullWidth
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(digitsOnly(e.target.value))}
              inputProps={{
                style: { fontSize: 36, letterSpacing: 16, textAlign: "center" },
                maxLength: PIN_LENGTH,
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={busy || pin.length !== PIN_LENGTH}
              sx={{ mt: 2, py: 1.5, fontSize: 18 }}
            >
              {t("enter")}
            </Button>
          </Box>
        )}

        {step === "new-pin" && student && (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); submitNewPin(); }}>
            <Typography sx={{ mb: 1.5 }}>
              {student.name}: {t("choosePinExplanation")}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              type="password"
              inputMode="numeric"
              label={t("newPin")}
              value={pin}
              onChange={(e) => setPin(digitsOnly(e.target.value))}
              inputProps={{
                style: { fontSize: 28, letterSpacing: 12, textAlign: "center" },
                maxLength: PIN_LENGTH,
              }}
            />
            <TextField
              fullWidth
              type="password"
              inputMode="numeric"
              label={t("repeatPin")}
              value={confirmPin}
              onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
              sx={{ mt: 2 }}
              inputProps={{
                style: { fontSize: 28, letterSpacing: 12, textAlign: "center" },
                maxLength: PIN_LENGTH,
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={
                busy || pin.length !== PIN_LENGTH || confirmPin.length !== PIN_LENGTH
              }
              sx={{ mt: 2, py: 1.5, fontSize: 18 }}
            >
              {t("save")}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClassCodeLoginModal;
